/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, lazy, Suspense, useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { SpriteIcon } from '@/components/ui/sprite-icon';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/providers/auth';

const LoginSheet = lazy(() => import('@/components/header/login-sheet'));

export default function LoginSheetButton(): ReactElement | null {
    const { t: tHeader } = useTranslation('header');
    const [open, setOpen] = useState(false);
    const session = useAuth();
    const isAuthenticated = Boolean(session?.userType === 'registered' && session?.customerId);

    const handleOpenChange = useCallback((isOpen: boolean) => {
        setOpen(isOpen);
    }, []);

    if (!isAuthenticated) return null;

    return (
        <>
            <Button
                variant="ghost"
                className="cursor-pointer !p-1 m-1 hover:bg-header-menu-icon-hover-background group-data-[transparent=true]/header:hover:bg-header-transparent-menu-icon-hover-background! transition-opacity h-auto"
                aria-label={tHeader('signIn')}
                onClick={() => setOpen(true)}>
                <SpriteIcon
                    name="account"
                    className="size-8 group-data-[transparent=true]/header:text-white! group-data-[transparent=true]/header:fill-white!"
                />
            </Button>
            {open && (
                <Suspense fallback={null}>
                    <LoginSheet open={open} onOpenChange={handleOpenChange} />
                </Suspense>
            )}
        </>
    );
}
